import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import api from '../lib/api';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
    MapPin, Truck, Calendar as CalendarIcon, RefreshCw, 
    LogIn, LogOut, AlertTriangle, Filter, ChevronDown
} from 'lucide-react';
import { format } from 'date-fns';
import { el, enUS } from 'date-fns/locale';

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom icons for different event types
const createCustomIcon = (color, type) => {
    const iconHtml = `
        <div style="
            background-color: ${color};
            width: 32px;
            height: 32px;
            border-radius: 50% 50% 50% 0;
            transform: rotate(-45deg);
            display: flex;
            align-items: center;
            justify-content: center;
            border: 3px solid white;
            box-shadow: 0 2px 5px rgba(0,0,0,0.3);
        ">
            <span style="transform: rotate(45deg); font-size: 14px;">${type === 'checkin' ? '📥' : type === 'checkout' ? '📤' : '⚠️'}</span>
        </div>
    `;
    return L.divIcon({
        html: iconHtml,
        className: 'custom-marker',
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
    });
};

const checkInIcon = createCustomIcon('#22c55e', 'checkin');
const checkOutIcon = createCustomIcon('#8b5cf6', 'checkout');
const damageIcon = createCustomIcon('#ef4444', 'damage');

// Component to fit map bounds
function FitBounds({ markers }) {
    const map = useMap();
    
    useEffect(() => {
        if (markers.length > 0) {
            const bounds = L.latLngBounds(markers.map(m => [m.latitude, m.longitude]));
            map.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [markers, map]);
    
    return null;
}

export default function LiveFleetPage() {
    const { language } = useLanguage();
    const [loading, setLoading] = useState(true);
    const [fleetEvents, setFleetEvents] = useState([]);
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [drivers, setDrivers] = useState([]);
    
    // Filters
    const [selectedVehicle, setSelectedVehicle] = useState('all');
    const [selectedDriver, setSelectedDriver] = useState('all');
    const [selectedEventType, setSelectedEventType] = useState('all');
    const [dateRange, setDateRange] = useState({
        from: new Date(new Date().setDate(new Date().getDate() - 7)),
        to: new Date()
    });
    const [showFilters, setShowFilters] = useState(false);

    // Default center (Greece)
    const defaultCenter = [39.0742, 21.8243];
    const defaultZoom = 7;

    const fetchFleetData = useCallback(async () => {
        setLoading(true);
        try {
            // Fetch vehicles
            const vehiclesRes = await api.get('/vehicles');
            setVehicles(vehiclesRes.data || []);

            // Fetch drivers
            const driversRes = await api.get('/users?role=driver');
            setDrivers(driversRes.data || []);

            // Fetch inspections with location data
            const fromDate = format(dateRange.from, 'yyyy-MM-dd');
            const toDate = format(dateRange.to, 'yyyy-MM-dd');
            
            const inspectionsRes = await api.get(`/inspections?from_date=${fromDate}&to_date=${toDate}&limit=100`);
            const inspections = inspectionsRes.data || [];

            // Fetch damage reports
            const damageRes = await api.get(`/damage-reports?from_date=${fromDate}&to_date=${toDate}&limit=50`);
            const damages = damageRes.data || [];

            // Combine into fleet events
            const events = [];

            // Process inspections
            inspections.forEach(inspection => {
                if (inspection.checkin_location) {
                    events.push({
                        id: `checkin-${inspection.id}`,
                        type: 'checkin',
                        latitude: inspection.checkin_location.latitude,
                        longitude: inspection.checkin_location.longitude,
                        timestamp: inspection.created_at,
                        vehicle_id: inspection.vehicle_id,
                        vehicle_plate: inspection.vehicle?.plate_number || 'N/A',
                        driver_id: inspection.driver_id,
                        driver_name: inspection.driver?.name || 'N/A',
                        odometer: inspection.odometer_reading,
                        details: inspection
                    });
                }
                
                if (inspection.checkout_completed && inspection.checkout_location) {
                    events.push({
                        id: `checkout-${inspection.id}`,
                        type: 'checkout',
                        latitude: inspection.checkout_location.latitude,
                        longitude: inspection.checkout_location.longitude,
                        timestamp: inspection.checkout_date,
                        vehicle_id: inspection.vehicle_id,
                        vehicle_plate: inspection.vehicle?.plate_number || 'N/A',
                        driver_id: inspection.driver_id,
                        driver_name: inspection.driver?.name || 'N/A',
                        odometer: inspection.checkout_odometer,
                        details: inspection
                    });
                }
            });

            // Process damage reports
            damages.forEach(damage => {
                if (damage.location) {
                    events.push({
                        id: `damage-${damage.id}`,
                        type: 'damage',
                        latitude: damage.location.latitude,
                        longitude: damage.location.longitude,
                        timestamp: damage.created_at,
                        vehicle_id: damage.vehicle_id,
                        vehicle_plate: damage.vehicle?.plate_number || 'N/A',
                        driver_id: damage.reported_by,
                        driver_name: damage.reporter?.name || 'N/A',
                        description: damage.description,
                        severity: damage.severity,
                        details: damage
                    });
                }
            });

            // Sort by timestamp descending
            events.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            
            setFleetEvents(events);
            setFilteredEvents(events);
        } catch (error) {
            console.error('Failed to fetch fleet data:', error);
        } finally {
            setLoading(false);
        }
    }, [dateRange]);

    useEffect(() => {
        fetchFleetData();
    }, [fetchFleetData]);

    // Apply filters
    useEffect(() => {
        let filtered = [...fleetEvents];

        if (selectedVehicle !== 'all') {
            filtered = filtered.filter(e => e.vehicle_id === selectedVehicle);
        }

        if (selectedDriver !== 'all') {
            filtered = filtered.filter(e => e.driver_id === selectedDriver);
        }

        if (selectedEventType !== 'all') {
            filtered = filtered.filter(e => e.type === selectedEventType);
        }

        setFilteredEvents(filtered);
    }, [fleetEvents, selectedVehicle, selectedDriver, selectedEventType]);

    const formatEventTime = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp);
        return format(date, 'dd/MM/yyyy HH:mm', { locale: language === 'el' ? el : enUS });
    };

    const getEventIcon = (type) => {
        switch (type) {
            case 'checkin': return checkInIcon;
            case 'checkout': return checkOutIcon;
            case 'damage': return damageIcon;
            default: return checkInIcon;
        }
    };

    const getEventColor = (type) => {
        switch (type) {
            case 'checkin': return 'bg-green-500';
            case 'checkout': return 'bg-purple-500';
            case 'damage': return 'bg-red-500';
            default: return 'bg-gray-500';
        }
    };

    const getEventLabel = (type) => {
        switch (type) {
            case 'checkin': return language === 'el' ? 'Check-In' : 'Check-In';
            case 'checkout': return language === 'el' ? 'Check-Out' : 'Check-Out';
            case 'damage': return language === 'el' ? 'Αναφορά Βλάβης' : 'Damage Report';
            default: return type;
        }
    };

    return (
        <div className="p-4 lg:p-6 space-y-4">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                        <MapPin className="w-7 h-7 text-blue-600" />
                        {language === 'el' ? 'Live Fleet' : 'Live Fleet'}
                    </h1>
                    <p className="text-slate-500 text-sm mt-1">
                        {language === 'el' 
                            ? `${filteredEvents.length} συμβάντα στον χάρτη`
                            : `${filteredEvents.length} events on map`}
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={() => setShowFilters(!showFilters)}
                        className="flex items-center gap-2"
                    >
                        <Filter className="w-4 h-4" />
                        {language === 'el' ? 'Φίλτρα' : 'Filters'}
                        <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                    </Button>
                    <Button
                        onClick={fetchFleetData}
                        disabled={loading}
                        className="bg-blue-600 hover:bg-blue-500 text-white"
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        {language === 'el' ? 'Ανανέωση' : 'Refresh'}
                    </Button>
                </div>
            </div>

            {/* Filters Panel */}
            {showFilters && (
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Date Range */}
                        <div>
                            <label className="text-sm font-medium text-slate-700 mb-1 block">
                                {language === 'el' ? 'Περίοδος' : 'Date Range'}
                            </label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {dateRange.from && dateRange.to ? (
                                            `${format(dateRange.from, 'dd/MM')} - ${format(dateRange.to, 'dd/MM')}`
                                        ) : (
                                            <span>{language === 'el' ? 'Επιλέξτε ημερομηνίες' : 'Pick dates'}</span>
                                        )}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0 bg-white" align="start">
                                    <Calendar
                                        mode="range"
                                        selected={dateRange}
                                        onSelect={(range) => range && setDateRange(range)}
                                        numberOfMonths={1}
                                        locale={language === 'el' ? el : enUS}
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>

                        {/* Vehicle Filter */}
                        <div>
                            <label className="text-sm font-medium text-slate-700 mb-1 block">
                                {language === 'el' ? 'Όχημα' : 'Vehicle'}
                            </label>
                            <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
                                <SelectTrigger>
                                    <SelectValue placeholder={language === 'el' ? 'Όλα' : 'All'} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{language === 'el' ? 'Όλα τα οχήματα' : 'All vehicles'}</SelectItem>
                                    {vehicles.map(v => (
                                        <SelectItem key={v.id} value={v.id}>
                                            {v.plate_number} - {v.make} {v.model}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Driver Filter */}
                        <div>
                            <label className="text-sm font-medium text-slate-700 mb-1 block">
                                {language === 'el' ? 'Οδηγός' : 'Driver'}
                            </label>
                            <Select value={selectedDriver} onValueChange={setSelectedDriver}>
                                <SelectTrigger>
                                    <SelectValue placeholder={language === 'el' ? 'Όλοι' : 'All'} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{language === 'el' ? 'Όλοι οι οδηγοί' : 'All drivers'}</SelectItem>
                                    {drivers.map(d => (
                                        <SelectItem key={d.id} value={d.id}>
                                            {d.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Event Type Filter */}
                        <div>
                            <label className="text-sm font-medium text-slate-700 mb-1 block">
                                {language === 'el' ? 'Τύπος Συμβάντος' : 'Event Type'}
                            </label>
                            <Select value={selectedEventType} onValueChange={setSelectedEventType}>
                                <SelectTrigger>
                                    <SelectValue placeholder={language === 'el' ? 'Όλα' : 'All'} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{language === 'el' ? 'Όλα τα συμβάντα' : 'All events'}</SelectItem>
                                    <SelectItem value="checkin">Check-In</SelectItem>
                                    <SelectItem value="checkout">Check-Out</SelectItem>
                                    <SelectItem value="damage">{language === 'el' ? 'Βλάβες' : 'Damages'}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
            )}

            {/* Map and Events List */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Map */}
                <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" style={{ height: '500px' }}>
                    {loading ? (
                        <div className="h-full flex items-center justify-center">
                            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
                        </div>
                    ) : (
                        <MapContainer
                            center={defaultCenter}
                            zoom={defaultZoom}
                            style={{ height: '100%', width: '100%', zIndex: 1 }}
                            scrollWheelZoom={true}
                        >
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            {filteredEvents.length > 0 && <FitBounds markers={filteredEvents} />}
                            {filteredEvents.map(event => (
                                <Marker
                                    key={event.id}
                                    position={[event.latitude, event.longitude]}
                                    icon={getEventIcon(event.type)}
                                >
                                    <Popup>
                                        <div className="min-w-[200px]">
                                            <div className={`${getEventColor(event.type)} text-white px-2 py-1 rounded-t-lg -mx-3 -mt-2 mb-2`}>
                                                <span className="font-semibold">{getEventLabel(event.type)}</span>
                                            </div>
                                            <div className="space-y-1 text-sm">
                                                <p><strong><Truck className="w-3 h-3 inline mr-1" />{language === 'el' ? 'Όχημα:' : 'Vehicle:'}</strong> {event.vehicle_plate}</p>
                                                <p><strong>{language === 'el' ? 'Οδηγός:' : 'Driver:'}</strong> {event.driver_name}</p>
                                                <p><strong>{language === 'el' ? 'Ώρα:' : 'Time:'}</strong> {formatEventTime(event.timestamp)}</p>
                                                {event.odometer && (
                                                    <p><strong>{language === 'el' ? 'Χιλιόμετρα:' : 'Odometer:'}</strong> {event.odometer.toLocaleString()} km</p>
                                                )}
                                                {event.description && (
                                                    <p><strong>{language === 'el' ? 'Περιγραφή:' : 'Description:'}</strong> {event.description}</p>
                                                )}
                                            </div>
                                        </div>
                                    </Popup>
                                </Marker>
                            ))}
                        </MapContainer>
                    )}
                </div>

                {/* Events List */}
                <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" style={{ height: '500px' }}>
                    <div className="bg-slate-50 border-b border-slate-200 p-3">
                        <h3 className="font-semibold text-slate-800">
                            {language === 'el' ? 'Πρόσφατα Συμβάντα' : 'Recent Events'}
                        </h3>
                    </div>
                    <div className="overflow-y-auto" style={{ height: 'calc(100% - 52px)' }}>
                        {filteredEvents.length === 0 ? (
                            <div className="p-4 text-center text-slate-500">
                                {language === 'el' 
                                    ? 'Δεν βρέθηκαν συμβάντα για τα επιλεγμένα φίλτρα'
                                    : 'No events found for selected filters'}
                            </div>
                        ) : (
                            <div className="divide-y divide-slate-100">
                                {filteredEvents.slice(0, 20).map(event => (
                                    <div key={event.id} className="p-3 hover:bg-slate-50 transition-colors">
                                        <div className="flex items-start gap-3">
                                            <div className={`w-8 h-8 rounded-full ${getEventColor(event.type)} flex items-center justify-center flex-shrink-0`}>
                                                {event.type === 'checkin' && <LogIn className="w-4 h-4 text-white" />}
                                                {event.type === 'checkout' && <LogOut className="w-4 h-4 text-white" />}
                                                {event.type === 'damage' && <AlertTriangle className="w-4 h-4 text-white" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-slate-800 text-sm">
                                                    {getEventLabel(event.type)}
                                                </p>
                                                <p className="text-xs text-slate-500 truncate">
                                                    {event.vehicle_plate} • {event.driver_name}
                                                </p>
                                                <p className="text-xs text-slate-400 mt-1">
                                                    {formatEventTime(event.timestamp)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Legend */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <h4 className="font-medium text-slate-700 mb-3">
                    {language === 'el' ? 'Υπόμνημα' : 'Legend'}
                </h4>
                <div className="flex flex-wrap gap-4">
                    <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                            <LogIn className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-sm text-slate-600">Check-In</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center">
                            <LogOut className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-sm text-slate-600">Check-Out</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
                            <AlertTriangle className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-sm text-slate-600">{language === 'el' ? 'Αναφορά Βλάβης' : 'Damage Report'}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
